import { ArrowLeft, Calendar, ExternalLink, Share } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useNews } from '../hooks/useContent';
import { formatDate, getRelativeTime, processMarkdown } from '../lib/content';
import { loadNewsPost } from '../lib/contentLoader';
import { topicLabels } from '../lib/router';
import { NewsPost } from '../types/content';

export function NewsPostPage() {
  const { slug, year, month } = useParams<{ 
    slug: string; 
    year?: string; 
    month?: string; 
  }>();
  const [post, setPost] = useState<NewsPost | null>(null);
  const [processedContent, setProcessedContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareMessage, setShareMessage] = useState<string>('');
  const { posts } = useNews();

  useEffect(() => {
    if (!slug) return;
    
    const loadPost = async () => {
      try {
        setLoading(true);
        const postData = await loadNewsPost(slug, year, month);
        setPost(postData);
        if (!postData) {
          setError('Article not found');
        } else if (postData.content) {
          // Process markdown content to HTML
          const html = await processMarkdown(postData.content);
          setProcessedContent(html);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  // Get recent posts excluding current post, sorted by date (newest first)
  const recentPosts = posts
    .filter(p => p.slug !== slug)
    .sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime())
    .slice(0, 5);

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareTitle = post?.title || 'Check out this article';
    const shareText = post?.dek || 'Interesting article about AI literacy and prompt engineering';

    // Check if the Web Share API is available
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        setShareMessage('Shared successfully!');
      } catch (err) {
        // User cancelled or error occurred
        if ((err as Error).name !== 'AbortError') {
          // Fallback to clipboard
          copyToClipboard();
        }
      }
    } else {
      // Fallback to copying link to clipboard
      copyToClipboard();
    }

    // Clear message after 2 seconds
    setTimeout(() => setShareMessage(''), 2000);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareMessage('Link copied to clipboard!');
    } catch (err) {
      setShareMessage('Failed to copy link');
    }
  };

  if (loading) {
    return (
      <div className="container py-16">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-3/4 mb-4" />
            <div className="h-4 bg-muted rounded w-1/2 mb-8" />
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded" />
              <div className="h-4 bg-muted rounded w-5/6" />
              <div className="h-4 bg-muted rounded w-4/5" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <>
        <Helmet>
          <title>Article Not Found - Promptliteracy.ai</title>
        </Helmet>
        
        <div className="container py-16 text-center">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-8">{error || 'This article could not be found.'}</p>
          <Button variant="outline" size="sm" asChild className="border-slate-300 btn-blue-hover transition-colors">
            <Link to="/news">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to News
            </Link>
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} - Promptliteracy.ai</title>
        <meta name="description" content={post.dek} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.dek} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.published_at} />
        <meta property="article:author" content="Promptliteracy.ai" />
        {post.topics.map(topic => (
          <meta key={topic} property="article:tag" content={topic} />
        ))}
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container py-8 max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-8 text-sm">
            <Link to="/" className="text-muted-foreground hover:text-foreground link-underline">
              Home
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <Link to="/news" className="text-muted-foreground hover:text-foreground link-underline">
              News
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground font-medium">{post.title}</span>
          </nav>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Article Container */}
            <article className="lg:col-span-2 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-border/40 overflow-hidden card-accent">
            {/* Article Header */}
            <header className="px-8 pt-8 pb-6 border-b border-border/20">
              <h1 className="text-3xl md:text-4xl font-bold mb-6 leading-tight gradient-text-tech">
                {post.title}
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed font-medium">
                {post.dek}
              </p>

              {/* Article Meta */}
              <div className="flex flex-wrap items-center gap-4 mb-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-3 py-2 rounded-full">
                  <Calendar className="h-4 w-4" />
                  <span className="font-medium">Published:</span>
                  <span>{formatDate(post.published_at)}</span>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 px-3 py-2 rounded-full">
                  <span className="font-medium">Time ago:</span>
                  <span>{getRelativeTime(post.published_at)}</span>
                </div>
                
                <div className="flex items-center gap-2 text-sm bg-primary/10 px-3 py-2 rounded-full">
                  <span className="text-muted-foreground font-medium">Source:</span>
                  <a
                    href={post.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                  >
                    {post.source_name}
                  </a>
                  <ExternalLink className="h-3 w-3 text-primary" />
                </div>
              </div>

              {/* Tags */}
              <div className="flex items-center mb-6">
                <span className="text-sm font-medium text-muted-foreground mr-3">Topics:</span>
                {post.topics.map((topic, index) => (
                  <span key={topic}>
                    <Link 
                      to={`/tag/${topic}`}
                      className="text-sm text-secondary hover:text-primary link-underline cursor-pointer transition-colors font-medium"
                    >
                      {topicLabels[topic] || topic.replace('-', ' ')}
                    </Link>
                    {index < post.topics.length - 1 && (
                      <span className="text-muted-foreground mx-2">|</span>
                    )}
                  </span>
                ))}
              </div>

              {/* Editor's Note */}
              {post.curator_note && (
                <div className="border-l-4 border-primary bg-primary/5 px-6 py-4 rounded-r-lg mb-6">
                  <h3 className="font-semibold text-primary mb-2 text-sm uppercase tracking-wide">Editor's Note</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground italic">
                    {post.curator_note}
                  </p>
                </div>
              )}
            </header>

            {/* Article Content */}
            {processedContent && (
              <div className="px-8 py-8">
                <div className="prose prose-lg max-w-none leading-relaxed">
                  <div dangerouslySetInnerHTML={{ __html: processedContent }} />
                </div>
              </div>
            )}

            {/* Article Footer */}
            <footer className="px-8 py-6 bg-muted/20 border-t border-border/20">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <Button asChild className="btn-tech">
                  <a href={post.source_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Read Original Article
                  </a>
                </Button>
                
                <div className="flex items-center gap-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="btn-blue-hover"
                    onClick={handleShare}
                  >
                    <Share className="mr-2 h-4 w-4" />
                    {shareMessage || 'Share Article'}
                  </Button>
                </div>
              </div>
            </footer>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-[5rem]">
                <div className="sidebar-card">
                <h2 className="sidebar-header">
                  <div className="sidebar-header-accent"></div>
                  Recent News
                </h2>
                
                <div className="mb-4 pb-4 border-b border-primary/20">
                  <Button variant="outline" size="sm" asChild className="w-full btn-blue-hover text-xs border-primary/30 hover:border-primary py-1.5">
                    <Link to="/news">
                      <ArrowLeft className="mr-1.5 h-3 w-3" />
                      All News
                    </Link>
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {recentPosts.map((recentPost) => (
                    <Link
                      key={recentPost.slug}
                      to={`/news/${recentPost.slug}`}
                      className="block group link-underline"
                    >
                      <article className="relative pl-5 pb-1 hover:translate-x-1 transition-all duration-200">
                        <div className="absolute left-0 top-1 w-1.5 h-1.5 bg-secondary rounded-full group-hover:bg-primary transition-colors"></div>
                        <div className="absolute left-0.5 top-2.5 w-0.5 h-full bg-gradient-to-b from-secondary/30 to-transparent"></div>
                        
                        <h3 className="text-xs font-semibold leading-tight mb-2 line-clamp-2 text-foreground">
                          {recentPost.title}
                        </h3>
                        
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                            <Calendar className="h-2.5 w-2.5 flex-shrink-0" />
                            <span>{getRelativeTime(recentPost.published_at)}</span>
                            <span>•</span>
                            <ExternalLink className="h-2.5 w-2.5 flex-shrink-0" />
                            <span>{recentPost.source_name}</span>
                          </div>
                          
                          {recentPost.topics.length > 0 && (
                            <div>
                              {recentPost.topics.slice(0, 2).map((topic, index) => (
                                <span key={topic}>
                                  <Link
                                    to={`/tag/${topic}`}
                                    className="text-[10px] text-secondary link-underline hover:text-primary transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {topicLabels[topic] || topic.replace('-', ' ')}
                                  </Link>
                                  {index < Math.min(recentPost.topics.length, 2) - 1 && (
                                    <span className="text-muted-foreground mx-0.5">|</span>
                                  )}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </article>
                    </Link>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t border-primary/20">
                  <Button variant="outline" size="sm" asChild className="w-full btn-blue-hover text-xs border-primary/30 hover:border-primary py-1.5">
                    <Link to="/news">
                      <ArrowLeft className="mr-1.5 h-3 w-3" />
                      All News
                    </Link>
                  </Button>
                </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
}